/**
 * API e stato server della scoperta tematica (SPEC-015, F-066).
 *
 * ⚠️ Prezzi e capitalizzazioni sono STRINGHE decimali (ADR-0006): mai
 * `parseFloat` per calcoli, solo `lib/format.ts` per la visualizzazione. Un
 * valore `null` significa "non lo so" e si mostra «n/d» — per un titolo appena
 * scoperto è la norma, non un'anomalia.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { AssetDomain } from '@/lib/asset-domain';

export type MarketScope =
  | { kind: 'global' }
  | { kind: 'markets'; mics: string[] }
  | { kind: 'country'; country: string };

export type DiscoveryFilters = {
  /** L'ambito (SPEC-026 §5.1): il primo parametro. Assente nelle ricerche precedenti a F-148 = borsa. */
  domain?: AssetDomain;
  marketScope: MarketScope;
  capBands: Array<'mega' | 'large' | 'mid' | 'small'>;
  noveltyMonths?: number;
  trendingOnly: boolean;
  includeEtfs: boolean;
  assetTypes: Array<'stock' | 'etf' | 'crypto'>;
  excludeOwned: boolean;
  maxResults: number;
};

export type DiscoveryRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export type DiscoveryRun = {
  id: string;
  themeTitle: string;
  themeDescription: string | null;
  /** Letto dal backend dai filtri: `stock` per le ricerche precedenti a F-148. */
  domain: AssetDomain;
  filters: DiscoveryFilters;
  status: DiscoveryRunStatus;
  progress: string | null;
  /** Perché non ha trovato nulla: un vuoto deve dire quale vuoto è. */
  emptyReason: string | null;
  /** La valutazione è fallita: ci sono i titoli, non i punteggi. */
  scoringDegraded: boolean;
  errorMessage: string | null;
  stats: {
    candidatesFound?: number;
    afterDedupe?: number;
    resolved?: number;
    figiLookups?: number;
    dropped?: number;
    tokensUsed?: number;
  } | null;
  resultCount?: number;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  /**
   * La discendenza (F-140): `parent` = questa e' un aggiornamento di quella,
   * `refreshes` = questa e' stata aggiornata da quelle. Da qualunque anello
   * della catena si vedono quello prima e quelli dopo.
   */
  parent: { id: string; createdAt: string } | null;
  refreshes: Array<{ id: string; createdAt: string; status: string }>;
  refreshOptions: {
    autoRotate: boolean;
    wishlistOptions: { portfolioId: string; maxExposureAmount: string | null; maxPortfolioPercentage: string | null } | null;
  } | null;
};

export type DiscoveryResolution = 'resolved' | 'ambiguous' | 'unresolved';
export type DiscoveryCategory = 'top_cap' | 'emerging' | 'trending' | 'core';

export type DiscoveryResult = {
  id: string;
  /** Valorizzato solo per `resolved`: è ciò che rende la riga azionabile. */
  instrumentId: string | null;
  resolutionStatus: DiscoveryResolution;
  resolutionNote: string | null;
  displayName: string;
  symbol: string | null;
  mic: string | null;
  assetType: string | null;
  category: DiscoveryCategory;
  relevanceScore: number | null;
  rationale: string;
  risks: string | null;
  sources: Array<{ url: string; title: string | null; snippet: string | null; date: string | null }>;
  marketCap: string | null;
  lastPrice: string | null;
  change30d: string | null;
  /** Variazione su circa un anno: dice se ha una direzione, non solo come si muove ora. */
  change1y: string | null;
  /** Variazione sulla chiusura precedente (F-073). */
  change24h: string | null;
  /** Valuta di `lastPrice` E di `marketCap`: la card non la deduce più. */
  quoteCurrency: string | null;
  /** `null` = mai scaricate. Valorizzato con tutti i valori nulli = provato, provider muto. */
  quotesUpdatedAt: string | null;
  rank: number;
  addedToWatchlistAt: string | null;
  /** Candidati fra cui riconciliare a mano; vuoto per una riga già identificata. */
  mappingCandidates: DiscoveryMappingCandidate[];
};

export type DiscoveryMappingCandidate = {
  ticker: string;
  exchange: string | null;
  figi: string | null;
  currency: string | null;
  securityType: string | null;
  name: string | null;
};

/** Un fatto della cronologia di avanzamento (F-084). */
export type ProgressEvent = {
  kind: 'run' | 'stage' | 'llm';
  /** `null` quando il fatto è certo ma il suo istante non è registrato. */
  at: string | null;
  label: string;
  detail: string | null;
};

export type RunProgress = {
  llmCalls: number;
  /** `null` se nessuna chiamata ha riportato i token: diverso da zero. */
  totalTokens: number | null;
  callsWithoutUsage: number;
  events: ProgressEvent[];
};

export type DiscoveryRunDetail = DiscoveryRun & {
  /** Valorizzato SOLO mentre la ricerca è in corso: a run concluso è `null`. */
  progressDetail: RunProgress | null;
  results: DiscoveryResult[];
};

export type DiscoveryQuota = { used: number; limit: number; remaining: number; resetAt: string };

export type CreateRunInput = {
  themeTitle: string;
  themeDescription?: string;
  filters?: Partial<DiscoveryFilters>;
};

export const discoveryKeys = {
  runs: ['discovery', 'runs'] as const,
  run: (id: string) => ['discovery', 'run', id] as const,
  quota: ['discovery', 'quota'] as const,
};

export function useDiscoveryRuns() {
  return useQuery({
    queryKey: discoveryKeys.runs,
    queryFn: ({ signal }) => api.get<{ items: DiscoveryRun[]; total: number }>('/discovery/runs', signal),
  });
}

/**
 * Il polling si ferma da solo quando il run è finito: un intervallo fisso
 * continuerebbe a interrogare il server per sempre su una pagina lasciata
 * aperta. Stesso pattern di `AiAnalysisSection` (F-031).
 */
export function useDiscoveryRun(runId: string) {
  return useQuery({
    queryKey: discoveryKeys.run(runId),
    queryFn: ({ signal }) => api.get<DiscoveryRunDetail>(`/discovery/runs/${runId}`, signal),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'running' ? 4000 : false;
    },
  });
}

export function useDiscoveryQuota() {
  return useQuery({
    queryKey: discoveryKeys.quota,
    queryFn: ({ signal }) => api.get<DiscoveryQuota>('/discovery/quota', signal),
  });
}

export function useCreateDiscoveryRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRunInput) => api.post<DiscoveryRun>('/discovery/runs', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.runs });
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.quota });
    },
  });
}

export function useDeleteDiscoveryRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api.delete<void>(`/discovery/runs/${runId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.runs });
    },
  });
}

/**
 * Riconcilia a mano un titolo non identificato: crea lo strumento a catalogo e
 * lo lega al risultato. **Non** lo aggiunge alla watchlist — sono due gesti
 * distinti, e il titolo identificato serve comunque ai run futuri.
 */
/**
 * Un candidato descritto **con l'esito che avrebbe**: simbolo di quotazione,
 * mercato, valuta. `index` e' la chiave di selezione — il ticker no, perche'
 * venti candidati dello stesso titolo lo condividono.
 */
export type DescribedCandidate = {
  index: number;
  ticker: string;
  exchange: string | null;
  figi: string | null;
  compositeFigi: string | null;
  securityType: string | null;
  securityDescription: string | null;
  currency: string | null;
  name: string | null;
  providerSymbol: string | null;
  mic: string | null;
  marketName: string | null;
  resolvedCurrency: string | null;
  usable: boolean;
  reason: string | null;
};

export function useResultCandidates(runId: string, resultId: string) {
  return useQuery({
    queryKey: ['discovery', 'candidates', runId, resultId],
    queryFn: ({ signal }) =>
      api.get<{ items: DescribedCandidate[] }>(
        `/discovery/runs/${runId}/results/${resultId}/candidates`,
        signal,
      ),
  });
}

export function useReconcileResult(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      resultId: string;
      candidateIndex?: number;
      ticker?: string;
      currency?: string;
    }) =>
      api.post<DiscoveryResult>(`/discovery/runs/${runId}/results/${input.resultId}/reconcile`, {
        ...(input.candidateIndex !== undefined ? { candidateIndex: input.candidateIndex } : {}),
        ...(input.ticker ? { ticker: input.ticker } : {}),
        ...(input.currency ? { currency: input.currency } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.run(runId) });
    },
  });
}

/**
 * Marca il risultato come "aggiunto alla watchlist". Non è l'aggiunta: quella
 * passa da `POST /watchlist/items` (F-016), questo serve solo a mostrare lo
 * stato sulla card.
 */
export function useMarkResult(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { resultId: string; addedToWatchlist: boolean }) =>
      api.patch<DiscoveryResult>(`/discovery/runs/${runId}/results/${input.resultId}`, {
        addedToWatchlist: input.addedToWatchlist,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.run(runId) });
    },
  });
}

// -----------------------------------------------------------------------------
// Rotazione generale (F-075)
// -----------------------------------------------------------------------------

export type RotationStatus = 'queued' | 'running' | 'completed' | 'failed';

export type DiscoveryRotation = {
  id: string;
  runId: string;
  status: RotationStatus;
  progress: string | null;
  /** `picksTotal` minore di `groupsTotal`: qualche gruppo è fallito. */
  groupsTotal: number;
  picksTotal: number;
  emptyReason: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
};

export type RotationPick = {
  id: string;
  groupIndex: number;
  instrumentId: string | null;
  resultId: string | null;
  displayName: string;
  symbol: string | null;
  rationale: string;
  watchFor: string | null;
  risks: string | null;
  potential: number | null;
  /** I non eletti del gruppo, col motivo: è ciò che rende la scelta verificabile. */
  considered: Array<{ ref: string; name: string; reason: string }>;
  addedToWatchlistAt: string | null;
};

export type DiscoveryRotationDetail = DiscoveryRotation & {
  themeTitle: string;
  picks: RotationPick[];
};

export const rotationKeys = {
  ofRun: (runId: string) => ['discovery', 'rotations', runId] as const,
  detail: (id: string) => ['discovery', 'rotation', id] as const,
};

export function useRunRotations(runId: string) {
  return useQuery({
    queryKey: rotationKeys.ofRun(runId),
    queryFn: ({ signal }) => api.get<{ items: DiscoveryRotation[] }>(`/discovery/runs/${runId}/rotations`, signal),
    // Finché una rotazione è in corso la lista si aggiorna da sola; a lavoro
    // finito il polling si ferma, invece di interrogare per sempre una pagina
    // lasciata aperta.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((item) => item.status === 'queued' || item.status === 'running')
        ? 5000
        : false,
  });
}

export function useRotation(rotationId: string) {
  return useQuery({
    queryKey: rotationKeys.detail(rotationId),
    queryFn: ({ signal }) => api.get<DiscoveryRotationDetail>(`/discovery/rotations/${rotationId}`, signal),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'queued' || status === 'running' ? 5000 : false;
    },
  });
}

/**
 * Destinazione dei titoli eletti. Senza `addToWishlist` finiscono solo in
 * watchlist: candidarli all'acquisto e' un gesto in piu' e va chiesto.
 */
export type StartRotationInput = {
  addToWishlist: boolean;
  portfolioId?: string;
  maxExposureAmount?: string;
  maxPortfolioPercentage?: string;
};

// -------------------------------------------- Aggiornamento e confronto (F-140)

export type RefreshRunInput = {
  themeDescription?: string;
  filters?: Partial<DiscoveryFilters>;
  /** ⚠️ Deciso qui, dalla persona: la wishlist alimenta l'auto-trading. */
  autoRotate: boolean;
  wishlistOptions?: { portfolioId: string; maxExposureAmount?: string; maxPortfolioPercentage?: string };
};

export function useRefreshRun(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RefreshRunInput) => api.post<DiscoveryRun>(`/discovery/runs/${runId}/refresh`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.runs });
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.run(runId) });
      void queryClient.invalidateQueries({ queryKey: discoveryKeys.quota });
    },
  });
}

export type ComparisonRetained = {
  displayName: string;
  symbol: string | null;
  instrumentId: string | null;
  matchedBy: 'instrument' | 'name';
  rankBefore: number;
  rankAfter: number;
  scoreBefore: number | null;
  scoreAfter: number | null;
  priceBefore: string | null;
  priceAfter: string | null;
  currency: string | null;
  /** `null` con `priceChangeNote`: fra due valute diverse non si calcola. */
  priceChangePct: string | null;
  priceChangeNote: string | null;
  wasPicked: boolean;
};

export type ComparisonChanged = {
  displayName: string;
  symbol: string | null;
  instrumentId: string | null;
  rank: number;
  score: number | null;
  price: string | null;
  currency: string | null;
  wasPicked: boolean;
};

export type RunComparison = {
  runId: string;
  parentRunId: string;
  status: 'queued' | 'running' | 'completed' | 'degraded' | 'failed';
  /** I numeri, fatti dal codice. */
  stats: {
    elapsedDays: number;
    parentCount: number;
    childCount: number;
    retained: ComparisonRetained[];
    added: ComparisonChanged[];
    dropped: ComparisonChanged[];
    pickedStillPresent: number;
    pickedTotal: number;
  } | null;
  /** La lettura del modello. `null` in `degraded`: i numeri ci sono, il commento no. */
  summary: { headline: string; marketReading: string; retainedNotes: string[]; notableChanges: string[] } | null;
  llmModel: string | null;
  errorMessage: string | null;
  createdAt: string;
  completedAt: string | null;
};

export function useRunComparison(runId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['discovery', 'comparison', runId] as const,
    queryFn: ({ signal }) => api.get<{ comparison: RunComparison | null }>(`/discovery/runs/${runId}/comparison`, signal),
    enabled,
    // Finche' il confronto e' in coda o in corso si rilegge: e' una chiamata al
    // modello, dura qualche decina di secondi.
    refetchInterval: (query) => {
      const status = query.state.data?.comparison?.status;
      return status === 'queued' || status === 'running' || status === undefined ? 5_000 : false;
    },
  });
}

export function useStartRotation(runId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: StartRotationInput) =>
      api.post<DiscoveryRotation>(`/discovery/runs/${runId}/rotation`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rotationKeys.ofRun(runId) });
      // I titoli eletti entrano in watchlist: quella vista è ora superata.
      void queryClient.invalidateQueries({ queryKey: ['watchlist'] });
      // Se richiesta, la rotazione scrive anche in wishlist: quella vista e'
      // altrettanto superata.
      void queryClient.invalidateQueries({ queryKey: ['wishlist'] });
    },
  });
}
